import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';
import { CoreStore } from '../state/core.store';
import { SpinnerService } from '../services/spinner.service';
import { AuthSession } from '../auth/auth-session';

const PUBLIC_AUTH_PATHS = ['/auth/login', '/account/public'] as const;

export const SKIP_AUTHORIZATION = new HttpContextToken<boolean>(() => false);

function isPublicRequest(url: string): boolean {
  return PUBLIC_AUTH_PATHS.some((path) => url.startsWith(path));
}

function isUserManagementRequest(url: string, baseUrl: string): boolean {
  return url === baseUrl || url.startsWith(`${baseUrl}/`);
}

function isRelativeBackendRequest(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('/assets/');
}

/**
 * Antepone l'host dei computi (apiBaseUrl da config) alle richieste relative.
 * Le richieste user-manage sono giÃ  composte dai relativi servizi usando
 * userManagementBaseUrl, che puÃ² essere assoluto o same-origin.
 * Esclusi: URL assoluti, './...' (loader i18n), 'config.json' (senza slash iniziale).
 * Gestisce inoltre lo spinner di caricamento (show/hide) attorno a ogni richiesta.
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(CoreStore);
  const spinnerService = inject(SpinnerService);
  const session = inject(AuthSession);

  const loaded = store.loaded();
  const userManagementBaseUrl = loaded ? store.userManagementBaseUrl() : '';
  const userManagementRequest =
    !!userManagementBaseUrl && isUserManagementRequest(req.url, userManagementBaseUrl);
  const requestPath = userManagementRequest
    ? req.url.slice(userManagementBaseUrl.length) || '/'
    : req.url;
  const skipAuthorization = req.context.get(SKIP_AUTHORIZATION);
  const token = session.token;
  if (
    (userManagementRequest || isRelativeBackendRequest(req.url)) &&
    token &&
    !isPublicRequest(requestPath) &&
    !skipAuthorization
  ) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  const apiBaseUrl = loaded ? store.apiBaseUrl() : '';
  if (apiBaseUrl && isRelativeBackendRequest(req.url) && !userManagementRequest) {
    req = req.clone({ url: `${apiBaseUrl}${req.url}` });
  }

  spinnerService.show();
  return next(req).pipe(finalize(() => spinnerService.hide()));
};
